/**
 * Calendar Sync Orchestration Service
 * 
 * Manages background synchronization jobs for Google Calendar and
 * Microsoft Outlook/Teams integrations.
 * 
 * Design Principles:
 * - Non-blocking: All sync operations run in background
 * - Retry-safe: Failed operations can be safely retried
 * - Idempotent: Multiple syncs produce same result
 * - Rate-limited: Respects API rate limits
 * - Well-logged: Clear audit trail of all operations
 * 
 * @module CalendarSyncService
 */

import type { CalendarEvent } from '../types';
import type { AsyncResult, CalendarConnectionError, CalendarErrorCode } from './types';
import { GoogleCalendarAuthService } from './GoogleCalendarAuthService';
import { GoogleCalendarSyncService } from './GoogleCalendarSyncService';
import type { BatchSyncResult, PullChangesResult, SyncEventResult } from './GoogleCalendarSyncService';
import { MicrosoftCalendarAuthService } from './MicrosoftCalendarAuthService';
import { OutlookCalendarService } from './OutlookCalendarService';
import type { OutlookBatchSyncResult, OutlookSyncResult } from './OutlookCalendarService';
import { persistExternalSync } from './persistExternalSync';
import { applyAndPersistGooglePullResults } from './applyGooglePullResults';
import {
  normalizeExternalApiError,
  getProviderAwareRetryDelay,
  type NormalizedApiError
} from './normalizeExternalApiError';
import {
  auditGooglePush,
  auditGooglePull,
  auditMicrosoftPush,
} from './CalendarSyncAuditService';
import { supabase, isDemoMode } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Helper to get supabase client for audit
// ─────────────────────────────────────────────────────────────────────────────

function getAuditClient() {
  if (isDemoMode) {
    return null;
  }
  return supabase;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Minimum interval between sync operations (in milliseconds).
 * Prevents excessive API calls.
 */
const MIN_SYNC_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Default interval for automatic background sync (in milliseconds).
 */
const DEFAULT_AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Time window (ms) after a pull update during which push sync skips the event.
 * This prevents infinite push-pull loops when applying external changes.
 * Events updated from external pull within this window won't be pushed back.
 */
const PULL_TO_PUSH_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Maximum retry attempts for failed sync operations.
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Maximum events to sync in a single batch.
 */
const MAX_BATCH_SIZE = 50;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sync operation status.
 */
export type SyncStatus =
  | 'idle'
  | 'syncing'
  | 'success'
  | 'error'
  | 'rate_limited'
  | 'reauth_required';

/**
 * Sync direction for the operation.
 */
export type SyncOperationType =
  | 'push'      // AX1 → External
  | 'pull'      // External → AX1
  | 'full';     // Both directions

/**
 * State of a sync job for a specific provider.
 */
export interface ProviderSyncState {
  /** Current status */
  status: SyncStatus;

  /** Provider identifier */
  provider: 'google' | 'microsoft';

  /** Timestamp of last successful sync */
  lastSyncAt?: string;

  /** Timestamp of last sync attempt */
  lastAttemptAt?: string;

  /** Current retry count */
  retryCount: number;

  /** Error from last failed attempt */
  lastError?: string;

  /** Next scheduled sync time */
  nextSyncAt?: string;

  /** 
   * Whether provider requires re-authentication.
   * When true, auto-sync will skip this provider to avoid spam retries.
   */
  reauthRequired?: boolean;

  /** Events synced in last operation */
  lastSyncStats?: {
    pushed: number;
    pulled: number;
    errors: number;
  };
}

/**
 * Overall sync service state.
 */
export interface SyncServiceState {
  /** Whether auto-sync is enabled */
  autoSyncEnabled: boolean;

  /** Auto-sync interval in milliseconds */
  autoSyncIntervalMs: number;

  /** Google Calendar sync state */
  google: ProviderSyncState;

  /** Microsoft/Outlook sync state */
  microsoft: ProviderSyncState;

  /** Whether any sync is currently in progress */
  isSyncing: boolean;
}

/**
 * Result of a full sync operation.
 */
export interface FullSyncResult {
  /** Provider that was synced */
  provider: 'google' | 'microsoft';

  /** Whether sync completed successfully */
  success: boolean;

  /** Push results (AX1 → External) */
  pushResult?: BatchSyncResult | OutlookBatchSyncResult;

  /** Pull results (External → AX1) - Google only */
  pullResult?: PullChangesResult;

  /** Error if sync failed */
  error?: CalendarConnectionError;

  /** Duration of sync in milliseconds */
  durationMs: number;

  /** Timestamp */
  timestamp: string;
}

/**
 * Sync log entry.
 */
export interface SyncJobLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  provider: 'google' | 'microsoft' | 'system';
  operation: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Callback for sync events.
 */
export type SyncEventCallback = (
  event: 'started' | 'completed' | 'error' | 'progress',
  provider: 'google' | 'microsoft',
  data?: unknown
) => void;

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a structured error object.
 */
function createError(
  code: CalendarErrorCode,
  message: string,
  details?: string,
  recoverable = true,
  suggestedAction?: string
): CalendarConnectionError {
  return {
    code,
    message,
    details,
    recoverable,
    suggestedAction,
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Checks if enough time has passed since last sync.
 */
function canSync(lastSyncAt: string | undefined): boolean {
  if (!lastSyncAt) return true;

  const lastSync = new Date(lastSyncAt).getTime();
  const now = Date.now();

  return (now - lastSync) >= MIN_SYNC_INTERVAL_MS;
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar Sync Service Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Orchestrates background calendar synchronization.
 * 
 * Usage:
 * ```typescript
 * const syncService = CalendarSyncService.getInstance();
 * 
 * // Manual sync
 * await syncService.syncNow('google', events);
 * 
 * // Enable auto-sync
 * syncService.startAutoSync(events, 5 * 60 * 1000);
 * 
 * // Listen for events
 * syncService.onSyncEvent((event, provider, data) => {
 *   console.log(`${provider}: ${event}`, data);
 * });
 * ```
 */
export class CalendarSyncService {
  private static instance: CalendarSyncService | null = null;

  // Service dependencies
  private googleAuthService: GoogleCalendarAuthService;
  private googleSyncService: GoogleCalendarSyncService;
  private microsoftAuthService: MicrosoftCalendarAuthService;
  private outlookService: OutlookCalendarService;

  // Sync state
  private state: SyncServiceState;

  // Auto-sync timer
  private autoSyncTimer: ReturnType<typeof setInterval> | null = null;

  // Event listeners
  private eventListeners: Set<SyncEventCallback> = new Set();

  // Sync log
  private syncLog: SyncJobLogEntry[] = [];
  private readonly MAX_LOG_ENTRIES = 500;

  // Cached events reference (for auto-sync)
  private cachedEventsGetter: (() => CalendarEvent[]) | null = null;

  // ─────────────────────────────────────────────────────────────────────────
  // Constructor & Singleton
  // ─────────────────────────────────────────────────────────────────────────

  private constructor() {
    this.googleAuthService = GoogleCalendarAuthService.getInstance();
    this.googleSyncService = GoogleCalendarSyncService.getInstance();
    this.microsoftAuthService = MicrosoftCalendarAuthService.getInstance();
    this.outlookService = OutlookCalendarService.getInstance();

    this.state = {
      autoSyncEnabled: false,
      autoSyncIntervalMs: DEFAULT_AUTO_SYNC_INTERVAL_MS,
      google: this.createInitialProviderState('google'),
      microsoft: this.createInitialProviderState('microsoft'),
      isSyncing: false,
    };
  }

  /**
   * Get singleton instance of the service.
   */
  public static getInstance(): CalendarSyncService {
    if (!CalendarSyncService.instance) {
      CalendarSyncService.instance = new CalendarSyncService();
    }
    return CalendarSyncService.instance;
  }

  /**
   * Reset singleton instance (for testing purposes).
   */
  public static resetInstance(): void {
    if (CalendarSyncService.instance) {
      CalendarSyncService.instance.stopAutoSync();
      CalendarSyncService.instance = null;
    }
  }

  /**
   * Creates initial state for a provider.
   */
  private createInitialProviderState(provider: 'google' | 'microsoft'): ProviderSyncState {
    return {
      status: 'idle',
      provider,
      retryCount: 0,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Manual Sync
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Triggers an immediate sync for the specified provider.
   * Non-blocking - returns immediately and syncs in background.
   * 
   * @param provider - Calendar provider to sync
   * @param events - AX1 events to sync
   * @param operationType - Type of sync operation
   * @returns Promise that resolves when sync completes
   */
  public async syncNow(
    provider: 'google' | 'microsoft',
    events: CalendarEvent[],
    operationType: SyncOperationType = 'full'
  ): Promise<AsyncResult<FullSyncResult>> {
    const providerState = provider === 'google' ? this.state.google : this.state.microsoft;

    // Check if reauth is required - skip sync to avoid spam retries
    if (providerState.reauthRequired) {
      return {
        success: false,
        error: createError(
          'REAUTH_REQUIRED',
          `${provider === 'google' ? 'Google Calendar' : 'Microsoft'} requires re-authentication`,
          'Please reconnect in Settings',
          true,
          'Reconnect in Settings → Integrations'
        ),
      };
    }

    // Check if already syncing
    if (providerState.status === 'syncing') {
      return {
        success: false,
        error: createError(
          'RATE_LIMITED',
          `${provider} sync already in progress`,
          'Please wait for the current sync to complete',
          true
        ),
      };
    }

    // Check rate limiting
    if (!canSync(providerState.lastAttemptAt)) {
      const waitTime = Math.ceil(
        (MIN_SYNC_INTERVAL_MS - (Date.now() - new Date(providerState.lastAttemptAt!).getTime())) / 1000
      );

      return {
        success: false,
        error: createError(
          'RATE_LIMITED',
          `Please wait ${waitTime} seconds before syncing again`,
          'Rate limiting in effect',
          true
        ),
      };
    }

    // Execute sync
    if (provider === 'google') {
      return this.executeGoogleSync(events, operationType);
    } else {
      return this.executeOutlookSync(events);
    }
  }

  /**
   * Triggers sync for all connected providers.
   * 
   * @param events - AX1 events to sync
   * @returns Results for each provider
   */
  public async syncAll(
    events: CalendarEvent[]
  ): Promise<Map<'google' | 'microsoft', AsyncResult<FullSyncResult>>> {
    const results = new Map<'google' | 'microsoft', AsyncResult<FullSyncResult>>();

    // Check which providers are connected
    const googleConnected = this.googleAuthService.isConnected();
    const microsoftConnected = this.microsoftAuthService.isConnected();

    this.log('info', 'system', 'syncAll',
      `Starting sync for ${googleConnected ? 'Google' : ''}${googleConnected && microsoftConnected ? ' and ' : ''}${microsoftConnected ? 'Microsoft' : ''}`);

    // Sync in parallel
    const promises: Promise<void>[] = [];

    if (googleConnected) {
      promises.push(
        this.syncNow('google', events).then(result => {
          results.set('google', result);
        })
      );
    }

    if (microsoftConnected) {
      promises.push(
        this.syncNow('microsoft', events).then(result => {
          results.set('microsoft', result);
        })
      );
    }

    await Promise.all(promises);

    return results;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Specific Provider Sync
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Syncs events to Google Calendar.
   * Performs bidirectional sync: push AX1 events and pull Google changes.
   * 
   * @param events - AX1 events to sync
   * @returns Sync result
   */
  public async syncGoogleCalendar(
    events: CalendarEvent[]
  ): Promise<AsyncResult<FullSyncResult>> {
    return this.syncNow('google', events, 'full');
  }

  /**
   * Syncs events to Outlook Calendar.
   * One-way sync: AX1 → Outlook only.
   * 
   * @param events - AX1 events to sync
   * @returns Sync result
   */
  public async syncOutlookCalendar(
    events: CalendarEvent[]
  ): Promise<AsyncResult<FullSyncResult>> {
    return this.syncNow('microsoft', events, 'push');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Auto-Sync
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Starts automatic background sync.
   * 
   * @param eventsGetter - Function that returns current AX1 events
   * @param intervalMs - Sync interval in milliseconds (default 5 minutes)
   */
  public startAutoSync(
    eventsGetter: () => CalendarEvent[],
    intervalMs: number = DEFAULT_AUTO_SYNC_INTERVAL_MS
  ): void {
    // Stop existing timer
    this.stopAutoSync();

    this.cachedEventsGetter = eventsGetter;
    this.state.autoSyncEnabled = true;
    this.state.autoSyncIntervalMs = intervalMs;

    this.log('info', 'system', 'startAutoSync',
      `Auto-sync enabled with ${intervalMs / 1000}s interval`);

    // Start timer
    this.autoSyncTimer = setInterval(() => {
      this.executeAutoSync();
    }, intervalMs);

    // Update next sync time
    this.updateNextSyncTime('google');
    this.updateNextSyncTime('microsoft');
  }

  /**
   * Stops automatic background sync.
   */
  public stopAutoSync(): void {
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
      this.autoSyncTimer = null;
    }

    this.state.autoSyncEnabled = false;
    this.cachedEventsGetter = null;

    this.log('info', 'system', 'stopAutoSync', 'Auto-sync disabled');
  }

  /**
   * Checks if auto-sync is currently enabled.
   */
  public isAutoSyncEnabled(): boolean {
    return this.state.autoSyncEnabled;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: State & Events
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Gets the current sync service state.
   */
  public getState(): SyncServiceState {
    return {
      ...this.state,
      google: { ...this.state.google },
      microsoft: { ...this.state.microsoft },
      isSyncing: this.state.google.status === 'syncing' ||
        this.state.microsoft.status === 'syncing',
    };
  }

  /**
   * Gets the sync state for a specific provider.
   */
  public getProviderState(provider: 'google' | 'microsoft'): ProviderSyncState {
    return { ...(provider === 'google' ? this.state.google : this.state.microsoft) };
  }

  /**
   * Registers a callback for sync events.
   */
  public onSyncEvent(callback: SyncEventCallback): () => void {
    this.eventListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.eventListeners.delete(callback);
    };
  }

  /**
   * Gets the sync log entries.
   */
  public getSyncLog(limit = 100): SyncJobLogEntry[] {
    return this.syncLog.slice(-limit).reverse();
  }

  /**
   * Clears the sync log.
   */
  public clearSyncLog(): void {
    this.syncLog = [];
    this.log('info', 'system', 'clearSyncLog', 'Sync log cleared');
  }

  /**
   * Clears the reauth_required flag for a provider.
   * Called after successful re-connection to resume auto-sync.
   * 
   * @param provider - Provider to clear reauth flag for
   */
  public clearReauthRequired(provider: 'google' | 'microsoft'): void {
    const state = provider === 'google' ? this.state.google : this.state.microsoft;

    if (state.reauthRequired) {
      state.reauthRequired = false;
      state.status = 'idle';
      state.retryCount = 0;
      state.lastError = undefined;

      this.log('info', provider, 'clearReauthRequired',
        `Re-authentication flag cleared for ${provider}`);
    }
  }

  /**
   * Checks if a provider requires re-authentication.
   * 
   * @param provider - Provider to check
   * @returns true if reauth is required
   */
  public isReauthRequired(provider: 'google' | 'microsoft'): boolean {
    const state = provider === 'google' ? this.state.google : this.state.microsoft;
    return state.reauthRequired === true;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Loop Prevention
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Filters out events that were recently updated from an external pull.
   * This prevents infinite push-pull sync loops.
   * 
   * Rules:
   * - Events with lastAppliedFromExternalAt within PULL_TO_PUSH_COOLDOWN_MS are skipped
   * - Events without external sync are passed through (not synced yet)
   * - Events without lastAppliedFromExternalAt are passed through (user edits)
   * 
   * @param events - Events to filter
   * @returns Events safe to push (not recently pulled from external)
   */
  private filterRecentlyPulledEvents(events: CalendarEvent[]): CalendarEvent[] {
    const now = Date.now();
    const filtered: CalendarEvent[] = [];
    let skippedCount = 0;

    for (const event of events) {
      const lastApplied = event.externalSync?.lastAppliedFromExternalAt;

      // No external sync = not synced yet, allow push
      if (!event.externalSync) {
        filtered.push(event);
        continue;
      }

      // No lastAppliedFromExternalAt = was never updated from external, allow push
      if (!lastApplied) {
        filtered.push(event);
        continue;
      }

      // Check if within cooldown window
      const appliedAt = new Date(lastApplied).getTime();
      const timeSinceApplied = now - appliedAt;

      if (timeSinceApplied < PULL_TO_PUSH_COOLDOWN_MS) {
        // Recently pulled from external - skip to prevent loop
        skippedCount++;
        continue;
      }

      // Outside cooldown window, allow push
      filtered.push(event);
    }

    if (skippedCount > 0) {
      this.log('debug', 'system', 'filterRecentlyPulledEvents',
        `Skipped ${skippedCount} events recently updated from external pull (loop prevention)`);
    }

    return filtered;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Sync Result Persistence
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Persists Google sync results to the database.
   * Called after each successful batch sync.
   */
  private async persistGoogleSyncResults(results: SyncEventResult[]): Promise<void> {
    const successfulResults = results.filter(
      r => (r.operation === 'created' || r.operation === 'updated') && r.syncInfo
    );

    if (successfulResults.length === 0) return;

    this.log('debug', 'google', 'persistGoogleSyncResults',
      `Persisting ${successfulResults.length} sync results to database`);


    let persisted = 0;
    let failed = 0;

    for (const result of successfulResults) {
      if (!result.syncInfo) continue;

      const persistResult = await persistExternalSync(
        result.ax1EventId,
        result.syncInfo
      );

      if (persistResult.success) {
        persisted++;
      } else {
        failed++;
        this.log('warn', 'google', 'persistGoogleSyncResults',
          `Failed to persist sync for event ${result.ax1EventId}: ${persistResult.error}`);
      }
    }

    this.log('debug', 'google', 'persistGoogleSyncResults',
      `Persisted ${persisted}/${successfulResults.length} sync results (${failed} failed)`);
  }

  /**
   * Persists Outlook sync results to the database.
   * Called after each successful batch sync.
   * Includes Teams meeting URL in externalSourceData.
   */
  private async persistOutlookSyncResults(results: OutlookSyncResult[]): Promise<void> {
    const successfulResults = results.filter(
      r => (r.operation === 'created' || r.operation === 'updated') && r.syncInfo
    );

    if (successfulResults.length === 0) return;

    this.log('debug', 'microsoft', 'persistOutlookSyncResults',
      `Persisting ${successfulResults.length} sync results to database`);

    let persisted = 0;
    let failed = 0;

    for (const result of successfulResults) {
      if (!result.syncInfo) continue;

      // Include Teams meeting URL if present
      const extraSourceData = result.teamsMeetingUrl
        ? { teamsMeetingUrl: result.teamsMeetingUrl }
        : undefined;

      const persistResult = await persistExternalSync(
        result.ax1EventId,
        result.syncInfo,
        extraSourceData
      );

      if (persistResult.success) {
        persisted++;
      } else {
        failed++;
        this.log('warn', 'microsoft', 'persistOutlookSyncResults',
          `Failed to persist sync for event ${result.ax1EventId}: ${persistResult.error}`);
      }
    }

    this.log('debug', 'microsoft', 'persistOutlookSyncResults',
      `Persisted ${persisted}/${successfulResults.length} sync results (${failed} failed)`);
  }

  /**
   * Audits Google sync results to the persistent audit log.
   * Fire-and-forget: errors are logged but don't block the sync flow.
   */
  private auditGoogleSyncResults(
    pushResult?: BatchSyncResult,
    pullResult?: PullChangesResult
  ): void {
    const client = getAuditClient();
    if (!client) {
      this.log('debug', 'google', 'auditGoogleSyncResults',
        'Demo mode or no client - skipping audit');
      return;
    }

    // Get current user ID for audit
    try {
      client.auth.getUser().then(({ data: { user } }) => {
        if (!user?.id) {
          this.log('warn', 'google', 'auditGoogleSyncResults',
            'No authenticated user - skipping audit');
          return;
        }

        const userId = user.id;

        // Audit push results
        if (pushResult?.results && pushResult.results.length > 0) {
          auditGooglePush(userId, pushResult.results);
          this.log('debug', 'google', 'auditGoogleSyncResults',
            `Queued ${pushResult.results.length} push audit entries`);
        }

        // Audit pull results
        if (pullResult?.results && pullResult.results.length > 0) {
          auditGooglePull(userId, pullResult.results);
          this.log('debug', 'google', 'auditGoogleSyncResults',
            `Queued ${pullResult.results.length} pull audit entries`);
        }
      }).catch((err) => {
        this.log('error', 'google', 'auditGoogleSyncResults',
          `Failed to get user for audit: ${err}`);
      });
    } catch (err) {
      this.log('error', 'google', 'auditGoogleSyncResults',
        `Audit client error: ${err}`);
    }
  }

  /**
   * Audits Microsoft/Outlook sync results to the persistent audit log.
   * Fire-and-forget: errors are logged but don't block the sync flow.
   */
  private auditMicrosoftSyncResults(
    pushResult?: OutlookBatchSyncResult
  ): void {
    const client = getAuditClient();
    if (!client) {
      this.log('debug', 'microsoft', 'auditMicrosoftSyncResults',
        'Demo mode or no client - skipping audit');
      return;
    }

    // Get current user ID for audit
    try {
      client.auth.getUser().then(({ data: { user } }) => {
        if (!user?.id) {
          this.log('warn', 'microsoft', 'auditMicrosoftSyncResults',
            'No authenticated user - skipping audit');
          return;
        }

        const userId = user.id;

        // Audit push results
        if (pushResult?.results && pushResult.results.length > 0) {
          auditMicrosoftPush(userId, pushResult.results);
          this.log('debug', 'microsoft', 'auditMicrosoftSyncResults',
            `Queued ${pushResult.results.length} push audit entries`);
        }
      }).catch((err) => {
        this.log('error', 'microsoft', 'auditMicrosoftSyncResults',
          `Failed to get user for audit: ${err}`);
      });
    } catch (err) {
      this.log('error', 'microsoft', 'auditMicrosoftSyncResults',
        `Audit client error: ${err}`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Sync Execution
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Executes Google Calendar sync.
   */
  private async executeGoogleSync(
    events: CalendarEvent[],
    operationType: SyncOperationType
  ): Promise<AsyncResult<FullSyncResult>> {
    const startTime = Date.now();
    const timestamp = new Date().toISOString();

    // Update state
    this.state.google.status = 'syncing';
    this.state.google.lastAttemptAt = timestamp;
    this.emitEvent('started', 'google');

    this.log('info', 'google', 'executeGoogleSync',
      `Starting ${operationType} sync with ${events.length} events`);

    try {
      // Check connection
      if (!this.googleAuthService.isConnected()) {
        throw new Error('Google Calendar is not connected');
      }

      let pushResult: BatchSyncResult | undefined;
      let pullResult: PullChangesResult | undefined;

      // Filter events for Google (meetings and milestones)
      const googleEligibleEvents = events.filter(
        e => e.type === 'meeting' || e.type === 'milestone'
      );

      // For push operations, filter out events recently updated from external pull
      // This prevents infinite push-pull loops
      const eventsForPush = (operationType === 'push' || operationType === 'full')
        ? this.filterRecentlyPulledEvents(googleEligibleEvents)
        : googleEligibleEvents;

      // Batch events if needed
      const batches = this.batchEvents(eventsForPush);

      // Push: AX1 → Google
      if (operationType === 'push' || operationType === 'full') {
        this.log('debug', 'google', 'executeGoogleSync',
          `Pushing ${googleEligibleEvents.length} events in ${batches.length} batches`);

        for (const batch of batches) {
          const batchResult = await this.googleSyncService.syncEventsToGoogle(batch);

          if (batchResult.success) {
            if (!pushResult) {
              pushResult = batchResult.data;
            } else {
              // Merge batch results
              pushResult.total += batchResult.data.total;
              pushResult.created += batchResult.data.created;
              pushResult.updated += batchResult.data.updated;
              pushResult.skipped += batchResult.data.skipped;
              pushResult.errors += batchResult.data.errors;
              pushResult.results.push(...batchResult.data.results);
            }

            // Persist sync results to database (non-blocking, errors logged)
            await this.persistGoogleSyncResults(batchResult.data.results);
          }

          // Rate limiting between batches
          await this.delay(500);
        }
      }

      // Pull: Google → AX1
      if (operationType === 'pull' || operationType === 'full') {
        // Only pull for events that have been synced
        const syncedEvents = events.filter(
          e => e.externalSync?.provider === 'google'
        );

        if (syncedEvents.length > 0) {
          this.log('debug', 'google', 'executeGoogleSync',
            `Pulling changes for ${syncedEvents.length} synced events`);

          const pullResultAsync = await this.googleSyncService.pullGoogleCalendarChanges(syncedEvents);

          if (pullResultAsync.success && pullResultAsync.data) {
            pullResult = pullResultAsync.data;

            // Apply and persist pull results to database
            // This updates events with Google changes and handles conflicts
            const eventsById = new Map(syncedEvents.map(e => [e.id, e]));
            const applyResult = await applyAndPersistGooglePullResults(
              pullResultAsync.data,
              eventsById
            );

            this.log('debug', 'google', 'executeGoogleSync',
              `Applied pull results: ${applyResult.updated} updated, ${applyResult.cancelled} cancelled, ${applyResult.conflictsFlagged} conflicts, ${applyResult.errors} errors`);
          }
        }
      }

      // Update state on success
      const durationMs = Date.now() - startTime;

      this.state.google.status = 'success';
      this.state.google.lastSyncAt = timestamp;
      this.state.google.retryCount = 0;
      this.state.google.lastError = undefined;
      this.state.google.lastSyncStats = {
        pushed: pushResult?.created ?? 0 + (pushResult?.updated ?? 0),
        pulled: pullResult?.updated ?? 0,
        errors: (pushResult?.errors ?? 0) + (pullResult?.errors ?? 0),
      };

      this.updateNextSyncTime('google');

      this.log('info', 'google', 'executeGoogleSync',
        `Sync completed in ${durationMs}ms: ${this.state.google.lastSyncStats.pushed} pushed, ${this.state.google.lastSyncStats.pulled} pulled`);

      const result: FullSyncResult = {
        provider: 'google',
        success: true,
        pushResult,
        pullResult,
        durationMs,
        timestamp,
      };

      this.emitEvent('completed', 'google', result);

      // Audit: persist sync operations to audit log (fire-and-forget)
      this.auditGoogleSyncResults(pushResult, pullResult);

      return { success: true, data: result };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const durationMs = Date.now() - startTime;

      // Detect if this is an auth/token failure requiring re-authentication
      const isAuthError = this.isReauthRequiredError(errorMessage);

      if (isAuthError) {
        // Mark provider as requiring reauth - auto-sync will skip until reconnected
        this.state.google.status = 'reauth_required';
        this.state.google.reauthRequired = true;
        this.state.google.lastError = 'Re-authentication required';

        this.log('warn', 'google', 'executeGoogleSync',
          'Token expired or refresh failed - re-authentication required');

        const syncError = createError(
          'REAUTH_REQUIRED',
          'Google Calendar requires re-authentication',
          'Your session has expired. Please reconnect in Settings.',
          true,
          'Go to Settings → Integrations → Reconnect'
        );

        const result: FullSyncResult = {
          provider: 'google',
          success: false,
          error: syncError,
          durationMs,
          timestamp,
        };

        this.emitEvent('error', 'google', result);

        // Do NOT schedule retry for auth errors
        return { success: false, error: syncError };
      }

      // Normalize the error for provider-aware handling
      const normalizedError = normalizeExternalApiError(error, { provider: 'google' });

      // Log rate-limit events clearly
      if (normalizedError.isRateLimit) {
        this.log('warn', 'google', 'executeGoogleSync:rate-limit',
          `Rate limit error: ${normalizedError.message}` +
          (normalizedError.retryAfterMs ? ` (retry after ${Math.round(normalizedError.retryAfterMs / 1000)}s)` : ''));
      }

      this.state.google.status = 'error';
      this.state.google.lastError = errorMessage;
      this.state.google.retryCount++;

      this.log('error', 'google', 'executeGoogleSync', errorMessage);

      const syncError = createError(
        normalizedError.isRateLimit ? 'RATE_LIMITED' : 'API_ERROR',
        normalizedError.message,
        errorMessage,
        this.state.google.retryCount < MAX_RETRY_ATTEMPTS
      );

      const result: FullSyncResult = {
        provider: 'google',
        success: false,
        error: syncError,
        durationMs,
        timestamp,
      };

      this.emitEvent('error', 'google', result);

      // Schedule retry if applicable, passing normalized error for provider-aware delay
      if (this.state.google.retryCount < MAX_RETRY_ATTEMPTS) {
        this.scheduleRetry('google', events, operationType, normalizedError);
      }

      return { success: false, error: syncError };
    }
  }

  /**
   * Executes Outlook Calendar sync.
   */
  private async executeOutlookSync(
    events: CalendarEvent[]
  ): Promise<AsyncResult<FullSyncResult>> {
    const startTime = Date.now();
    const timestamp = new Date().toISOString();

    // Update state
    this.state.microsoft.status = 'syncing';
    this.state.microsoft.lastAttemptAt = timestamp;
    this.emitEvent('started', 'microsoft');

    this.log('info', 'microsoft', 'executeOutlookSync',
      `Starting sync with ${events.length} events`);

    try {
      // Check connection
      if (!this.microsoftAuthService.isConnected()) {
        throw new Error('Microsoft Calendar is not connected');
      }

      // Filter events for Outlook (meetings only)
      const outlookEligibleEvents = events.filter(e => e.type === 'meeting');

      // Batch events if needed
      const batches = this.batchEvents(outlookEligibleEvents);

      let pushResult: OutlookBatchSyncResult | undefined;

      this.log('debug', 'microsoft', 'executeOutlookSync',
        `Pushing ${outlookEligibleEvents.length} meetings in ${batches.length} batches`);

      for (const batch of batches) {
        const batchResult = await this.outlookService.syncMeetingsToOutlook(batch, true);

        if (batchResult.success) {
          if (!pushResult) {
            pushResult = batchResult.data;
          } else {
            // Merge batch results
            pushResult.total += batchResult.data.total;
            pushResult.created += batchResult.data.created;
            pushResult.updated += batchResult.data.updated;
            pushResult.skipped += batchResult.data.skipped;
            pushResult.errors += batchResult.data.errors;
            pushResult.results.push(...batchResult.data.results);
          }

          // Persist sync results to database (non-blocking, errors logged)
          // Includes Teams meeting URL in externalSourceData
          await this.persistOutlookSyncResults(batchResult.data.results);
        }

        // Rate limiting between batches
        await this.delay(500);
      }

      // Update state on success
      const durationMs = Date.now() - startTime;

      this.state.microsoft.status = 'success';
      this.state.microsoft.lastSyncAt = timestamp;
      this.state.microsoft.retryCount = 0;
      this.state.microsoft.lastError = undefined;
      this.state.microsoft.lastSyncStats = {
        pushed: (pushResult?.created ?? 0) + (pushResult?.updated ?? 0),
        pulled: 0, // Outlook is one-way
        errors: pushResult?.errors ?? 0,
      };

      this.updateNextSyncTime('microsoft');

      this.log('info', 'microsoft', 'executeOutlookSync',
        `Sync completed in ${durationMs}ms: ${this.state.microsoft.lastSyncStats.pushed} pushed`);

      const result: FullSyncResult = {
        provider: 'microsoft',
        success: true,
        pushResult,
        durationMs,
        timestamp,
      };

      this.emitEvent('completed', 'microsoft', result);

      // Audit: persist sync operations to audit log (fire-and-forget)
      this.auditMicrosoftSyncResults(pushResult);

      return { success: true, data: result };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const durationMs = Date.now() - startTime;

      // Detect if this is an auth/token failure requiring re-authentication
      const isAuthError = this.isReauthRequiredError(errorMessage);

      if (isAuthError) {
        // Mark provider as requiring reauth - auto-sync will skip until reconnected
        this.state.microsoft.status = 'reauth_required';
        this.state.microsoft.reauthRequired = true;
        this.state.microsoft.lastError = 'Re-authentication required';

        this.log('warn', 'microsoft', 'executeOutlookSync',
          'Token expired or refresh failed - re-authentication required');

        const syncError = createError(
          'REAUTH_REQUIRED',
          'Microsoft requires re-authentication',
          'Your session has expired. Please reconnect in Settings.',
          true,
          'Go to Settings → Integrations → Reconnect'
        );

        const result: FullSyncResult = {
          provider: 'microsoft',
          success: false,
          error: syncError,
          durationMs,
          timestamp,
        };

        this.emitEvent('error', 'microsoft', result);

        // Do NOT schedule retry for auth errors
        return { success: false, error: syncError };
      }

      // Normalize the error for provider-aware handling
      const normalizedError = normalizeExternalApiError(error, { provider: 'microsoft' });

      // Log rate-limit events clearly with Retry-After if available
      if (normalizedError.isRateLimit) {
        this.log('warn', 'microsoft', 'executeOutlookSync:rate-limit',
          `Rate limit error: ${normalizedError.message}` +
          (normalizedError.retryAfterMs ? ` (Retry-After: ${Math.round(normalizedError.retryAfterMs / 1000)}s)` : ''));
      }

      this.state.microsoft.status = 'error';
      this.state.microsoft.lastError = errorMessage;
      this.state.microsoft.retryCount++;

      this.log('error', 'microsoft', 'executeOutlookSync', errorMessage);

      const syncError = createError(
        normalizedError.isRateLimit ? 'RATE_LIMITED' : 'API_ERROR',
        normalizedError.message,
        errorMessage,
        this.state.microsoft.retryCount < MAX_RETRY_ATTEMPTS
      );

      const result: FullSyncResult = {
        provider: 'microsoft',
        success: false,
        error: syncError,
        durationMs,
        timestamp,
      };

      this.emitEvent('error', 'microsoft', result);

      // Schedule retry if applicable, passing normalized error for provider-aware delay
      if (this.state.microsoft.retryCount < MAX_RETRY_ATTEMPTS) {
        this.scheduleRetry('microsoft', events, 'push', normalizedError);
      }

      return { success: false, error: syncError };
    }
  }

  /**
   * Executes auto-sync for all connected providers.
   */
  private async executeAutoSync(): Promise<void> {
    if (!this.cachedEventsGetter) {
      this.log('warn', 'system', 'executeAutoSync', 'No events getter configured');
      return;
    }

    // Skip if any sync is in progress
    if (this.state.google.status === 'syncing' || this.state.microsoft.status === 'syncing') {
      this.log('debug', 'system', 'executeAutoSync', 'Skipping - sync already in progress');
      return;
    }

    const events = this.cachedEventsGetter();

    this.log('debug', 'system', 'executeAutoSync',
      `Auto-sync triggered with ${events.length} events`);

    await this.syncAll(events);
  }

  /**
   * Schedules a retry for a failed sync.
   * Uses provider-aware delays and respects Retry-After from rate limit errors.
   */
  private scheduleRetry(
    provider: 'google' | 'microsoft',
    events: CalendarEvent[],
    operationType: SyncOperationType,
    normalizedError?: NormalizedApiError
  ): void {
    const state = provider === 'google' ? this.state.google : this.state.microsoft;

    // Use provider-aware delay with potential Retry-After from error
    const delay = getProviderAwareRetryDelay(provider, state.retryCount, normalizedError);

    // Log rate-limit events clearly
    if (normalizedError?.isRateLimit) {
      this.log('warn', provider, 'scheduleRetry:rate-limit',
        `Rate limit hit. Waiting ${Math.round(delay / 1000)}s before retry ${state.retryCount}/${MAX_RETRY_ATTEMPTS}` +
        (normalizedError.retryAfterMs ? ` (Retry-After: ${Math.round(normalizedError.retryAfterMs / 1000)}s)` : ''));
    } else {
      this.log('info', provider, 'scheduleRetry',
        `Scheduling retry ${state.retryCount}/${MAX_RETRY_ATTEMPTS} in ${Math.round(delay / 1000)}s`);
    }

    setTimeout(() => {
      this.syncNow(provider, events, operationType);
    }, delay);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Utilities
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Splits events into batches.
   */
  private batchEvents(events: CalendarEvent[]): CalendarEvent[][] {
    const batches: CalendarEvent[][] = [];

    for (let i = 0; i < events.length; i += MAX_BATCH_SIZE) {
      batches.push(events.slice(i, i + MAX_BATCH_SIZE));
    }

    return batches.length > 0 ? batches : [[]];
  }

  /**
   * Detects if an error indicates that re-authentication is required.
   * Matches common OAuth/token error patterns from Google and Microsoft.
   */
  private isReauthRequiredError(errorMessage: string): boolean {
    const lowerMessage = errorMessage.toLowerCase();

    // Common patterns indicating auth failure
    const authErrorPatterns = [
      'token expired',
      'token has been expired',
      'refresh token',
      'invalid_grant',
      'access_denied',
      'unauthorized',
      '401',
      'authentication required',
      'session expired',
      'invalid credentials',
      'token revoked',
      'consent_required',
      'interaction_required',
    ];

    return authErrorPatterns.some(pattern => lowerMessage.includes(pattern));
  }

  /**
   * Updates next sync time for a provider.
   */
  private updateNextSyncTime(provider: 'google' | 'microsoft'): void {
    if (!this.state.autoSyncEnabled) return;

    const nextSync = new Date(Date.now() + this.state.autoSyncIntervalMs);

    if (provider === 'google') {
      this.state.google.nextSyncAt = nextSync.toISOString();
    } else {
      this.state.microsoft.nextSyncAt = nextSync.toISOString();
    }
  }

  /**
   * Emits a sync event to all listeners.
   */
  private emitEvent(
    event: 'started' | 'completed' | 'error' | 'progress',
    provider: 'google' | 'microsoft',
    data?: unknown
  ): void {
    for (const callback of this.eventListeners) {
      try {
        callback(event, provider, data);
      } catch (error) {
        console.error('Error in sync event callback:', error);
      }
    }
  }

  /**
   * Adds an entry to the sync log.
   */
  private log(
    level: SyncJobLogEntry['level'],
    provider: SyncJobLogEntry['provider'],
    operation: string,
    message: string,
    details?: Record<string, unknown>
  ): void {
    const entry: SyncJobLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      provider,
      operation,
      message,
      details,
    };

    this.syncLog.push(entry);

    // Trim log if too large
    if (this.syncLog.length > this.MAX_LOG_ENTRIES) {
      this.syncLog = this.syncLog.slice(-this.MAX_LOG_ENTRIES);
    }

    // Console logging (skip debug in production)
    if (level !== 'debug') {
      const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      consoleMethod(`[CalendarSync:${provider}] ${operation}: ${message}`, details || '');
    }
  }

  /**
   * Simple delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Functions (Facade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Triggers immediate sync for Google Calendar.
 * 
 * @param events - AX1 events to sync
 * @returns Sync result
 */
export async function syncGoogleCalendar(
  events: CalendarEvent[]
): Promise<AsyncResult<FullSyncResult>> {
  const service = CalendarSyncService.getInstance();
  return service.syncGoogleCalendar(events);
}

/**
 * Triggers immediate sync for Outlook Calendar.
 * 
 * @param events - AX1 events to sync
 * @returns Sync result
 */
export async function syncOutlookCalendar(
  events: CalendarEvent[]
): Promise<AsyncResult<FullSyncResult>> {
  const service = CalendarSyncService.getInstance();
  return service.syncOutlookCalendar(events);
}

/**
 * Triggers immediate sync for all connected providers.
 * 
 * @param events - AX1 events to sync
 * @returns Map of results per provider
 */
export async function syncAllCalendars(
  events: CalendarEvent[]
): Promise<Map<'google' | 'microsoft', AsyncResult<FullSyncResult>>> {
  const service = CalendarSyncService.getInstance();
  return service.syncAll(events);
}

/**
 * Gets the current sync service state.
 */
export function getCalendarSyncState(): SyncServiceState {
  const service = CalendarSyncService.getInstance();
  return service.getState();
}

/**
 * Starts automatic background sync.
 * 
 * @param eventsGetter - Function that returns current AX1 events
 * @param intervalMs - Sync interval in milliseconds
 */
export function startCalendarAutoSync(
  eventsGetter: () => CalendarEvent[],
  intervalMs?: number
): void {
  const service = CalendarSyncService.getInstance();
  service.startAutoSync(eventsGetter, intervalMs);
}

/**
 * Stops automatic background sync.
 */
export function stopCalendarAutoSync(): void {
  const service = CalendarSyncService.getInstance();
  service.stopAutoSync();
}

/**
 * Registers a callback for sync events.
 * 
 * @param callback - Event callback
 * @returns Unsubscribe function
 */
export function onCalendarSyncEvent(callback: SyncEventCallback): () => void {
  const service = CalendarSyncService.getInstance();
  return service.onSyncEvent(callback);
}

/**
 * Gets the sync log entries.
 */
export function getCalendarSyncLog(limit?: number): SyncJobLogEntry[] {
  const service = CalendarSyncService.getInstance();
  return service.getSyncLog(limit);
}

/**
 * Clears the reauth_required flag for a provider.
 * Call this after successful re-connection to resume auto-sync.
 * 
 * @param provider - Provider to clear reauth flag for
 */
export function clearCalendarReauthRequired(provider: 'google' | 'microsoft'): void {
  const service = CalendarSyncService.getInstance();
  service.clearReauthRequired(provider);
}

/**
 * Checks if a provider requires re-authentication.
 * 
 * @param provider - Provider to check
 * @returns true if reauth is required
 */
export function isCalendarReauthRequired(provider: 'google' | 'microsoft'): boolean {
  const service = CalendarSyncService.getInstance();
  return service.isReauthRequired(provider);
}

// Default export
export default CalendarSyncService;
